import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { setClerkAuthInfo, clearClerkAuthInfo } from '../../store/defaultBaseQuery';
import { useGetUserAttributesQuery } from '../../store/userApi';
import { useDispatch } from 'react-redux';
import { setUser, clearUser } from '../../store/userSlice';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';


const ClerkAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthSetupComplete, setIsAuthSetupComplete] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  
  const excludedRedirectPaths = [
    '/subscription/pro', 
    '/direct-pro-signup',
    '/signup/verify',
    '/subscription/',  // Any subscription-related route
    '/checkout/',      // Any checkout-related route
    '/payment/',       // Any payment-related route
    '/billing/'        // Any billing-related route
  ];
  
  
  const { 
    data: userAttributes, 
    refetch, 
    error: userAttributesError, 
    isLoading,
    isSuccess,
    isUninitialized
  } = useGetUserAttributesQuery(undefined, {
    
    skip: !isSignedIn || !isAuthSetupComplete || !(window as any).__clerk_jwt,
    
    pollingInterval: 5 * 60 * 1000,
    
    refetchOnMountOrArgChange: true,
  });

  // Helper function to safely call refetch only when the query has been started
  const safeRefetch = useCallback(() => {
    if (!isUninitialized && isSignedIn && isAuthSetupComplete && (window as any).__clerk_jwt) {
      console.log('[Auth] Safely refetching user data');
      refetch();
    } else {
      console.log('[Auth] Skipping refetch - query not started or conditions not met', {
        isUninitialized,
        isSignedIn,
        isAuthSetupComplete,
        hasClerkJwt: !!(window as any).__clerk_jwt
      });
    }
  }, [isUninitialized, isSignedIn, isAuthSetupComplete, refetch]);

  
  useEffect(() => {
  
    if (isSignedIn && isAuthSetupComplete && userAttributes) {
      const currentPath = location.pathname;
      const isLoginOrSignup = currentPath === '/login' || 
                              currentPath === '/signup' || 
                              currentPath.startsWith('/login/') || 
                              currentPath.startsWith('/signup/');
      
  
      const isExcludedPath = excludedRedirectPaths.some(path => 
        currentPath.startsWith(path)
      );
      
  
      if (isLoginOrSignup && !isExcludedPath) {
        console.log('[Auth] Active session detected on login/signup page. Redirecting to dashboard.');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isSignedIn, isAuthSetupComplete, userAttributes, location.pathname, navigate]);

  
  useEffect(() => {
    if (!isSignedIn && isAuthSetupComplete) {
      clearClerkAuthInfo();
      dispatch(clearUser());
      console.log('[Auth] User signed out, cleared auth info');
    }
  }, [isSignedIn, isAuthSetupComplete, dispatch]);

  
  useEffect(() => {
    const setupAuth = async () => {
      if (isSignedIn && user) {
        try {
          setAuthError(null);
          
  
          const token = await getToken();
          
          if (!token) {
            console.error('[Auth] No token available from Clerk');
            setAuthError('No authentication token available');
            return;
          }
          
  
          setClerkAuthInfo(
            token, 
            user.id,
            user.primaryEmailAddress?.emailAddress
          );
          
          console.log('[Auth] Clerk token set successfully:', {
            userId: user.id,
            email: user.primaryEmailAddress?.emailAddress
          });
          
          setIsAuthSetupComplete(true);
          
          // Note: safeRefetch will be called automatically when the query conditions are met
          // due to the useEffect hook and skip conditions changing
        } catch (error) {
          console.error('[Auth] Error setting Clerk token:', error);
          setAuthError('Error during authentication setup');
        }
      } else if (!isSignedIn) {
  
        setIsAuthSetupComplete(true);
      }
    };
    
    setupAuth();
  }, [isSignedIn, user, getToken, safeRefetch]);

  // Effect to trigger initial data fetch when auth setup is complete
  useEffect(() => {
    if (isSignedIn && isAuthSetupComplete && (window as any).__clerk_jwt && isUninitialized) {
      console.log('[Auth] Auth setup complete, triggering initial user data fetch');
      safeRefetch();
    }
  }, [isSignedIn, isAuthSetupComplete, isUninitialized, safeRefetch]);
  
  
  useEffect(() => {
    if (isSuccess && userAttributes) {
      dispatch(setUser(userAttributes));
      console.log('[Auth] User data synced with backend:', {
        id: userAttributes.id,
        email: userAttributes.email,
        plan: userAttributes.plan || 'Unknown',
        hasActiveSubscription: userAttributes.hasActiveSubscription
      });
    } else if (userAttributesError) {
      console.error('[Auth] Error fetching user data:', userAttributesError);
      
      // If there's an error, but we have Clerk user information, create a minimal user object
      // This ensures the app can still function even when the backend fails
      if (isSignedIn && user) {
        console.log('[Auth] Creating fallback user object from Clerk data');
        const fallbackUser = {
          id: user.id,
          email: user.primaryEmailAddress?.emailAddress || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          plan: 'FREE', // Default to FREE plan
          hasActiveSubscription: false,
          isVerified: true,
          role: 'USER',
          prompts: []
        };
        
        // Set the fallback user in Redux
        dispatch(setUser(fallbackUser as any));
      }
      
  
      if (userAttributesError.toString().includes('401')) {
        console.log('[Auth] Session expired or invalid, refreshing token');
  
        const refreshAuth = async () => {
          if (isSignedIn && user) {
            try {
              const newToken = await getToken({ skipCache: true });
              if (newToken) {
                setClerkAuthInfo(
                  newToken, 
                  user.id,
                  user.primaryEmailAddress?.emailAddress
                );
                console.log('[Auth] Token refreshed');
  
                safeRefetch();
              }
            } catch (e) {
              console.error('[Auth] Failed to refresh token:', e);
            }
          }
        };
        refreshAuth();
      }
    }
  }, [userAttributes, userAttributesError, isSuccess, dispatch, isSignedIn, user, getToken, safeRefetch]);

  
  if (authError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error" variant="body1">
          Authentication error: {authError}. Please try refreshing the page.
        </Typography>
      </Box>
    );
  }
  
  
  if (isSignedIn && isAuthSetupComplete && isLoading && !userAttributes) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return <>{children}</>;
};

export default ClerkAuthProvider; 